import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Seo from '../../components/Seo';
import ITTrainingHeader from '../../components/ITTrainingHeader';
import ITTrainingFooter from '../../components/ITTrainingFooter';
import { getCourseBySlug } from '../../data/courseData';

const categoryLabel = {
  programming: 'Programming Courses',
  'non-programming': 'Non-Programming Courses',
  special: 'Special Training',
};

const categoryPath = {
  programming: '/it-training/programming',
  'non-programming': '/it-training/non-programming',
  special: '/it-training/special',
};

function CourseDetail() {
  const { slug } = useParams();
  const course = getCourseBySlug(slug);

  // 404 — redirect to IT Training home if course not found
  if (!course) return <Navigate to="/it-training" replace />;

  const catLabel = categoryLabel[course.category] || 'Courses';
  const catPath  = categoryPath[course.category] || '/it-training';

  return (
    <div className="itp-shell">
      <Seo
        title={`${course.title} | IT Training Ahmedabad | Vedanskrix`}
        description={`${course.tagline}. ${course.duration} course at Vedanskrix Ahmedabad. ${course.level} level. Includes live projects, certificate & job placement assistance.`}
        keywords={[
          course.title,
          `${course.title} Ahmedabad`,
          `${course.title} institute`,
          'IT training Ahmedabad',
          'Vedanskrix IT training',
          'job placement assistance',
          'programming course Gujarat',
          ...course.career
        ]}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: course.title,
          description: course.description,
          provider: {
            '@type': 'Organization',
            name: 'Vedanskrix Web LLP',
            sameAs: 'https://vedanskrix.com'
          },
          courseMode: ['onsite', 'online'],
          educationalLevel: course.level,
          timeRequired: `P${course.duration.replace(/[^0-9]/g, '')}M`,
          offers: {
            '@type': 'Offer',
            category: 'IT Training',
            availability: 'https://schema.org/InStock'
          },
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'Blended',
            location: {
              '@type': 'Place',
              name: 'Vedanskrix IT Training Centre',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Ahmedabad',
                addressRegion: 'Gujarat',
                addressCountry: 'IN'
              }
            }
          }
        }}
      />
      <ITTrainingHeader />

      {/* Hero */}
      <section className="itcd-hero">
        <div className="itcd-hero__overlay" />
        <div className="itp-container itcd-hero__content">
          <div className="itpage-hero__crumb" style={{ marginBottom: '14px' }}>
            <Link to="/it-training">Home</Link>
            <span> › </span>
            <Link to={catPath}>{catLabel}</Link>
            <span> › </span>
            <span>{course.title}</span>
          </div>
          <div className="itcd-hero__icon">{course.icon}</div>
          <h1 className="itcd-hero__title">{course.title}</h1>
          <p className="itcd-hero__tagline">{course.tagline}</p>
          <div className="itcd-hero__badges">
            <span className="itcd-badge itcd-badge--duration">⏱ {course.duration}</span>
            <span className="itcd-badge itcd-badge--level">🎯 {course.level}</span>
            <span className="itcd-badge itcd-badge--fee">💰 {course.fee}</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="itcd-body">
        <div className="itp-container itcd-body__grid">

          {/* Left: details */}
          <div className="itcd-main">

            {/* About the course */}
            <div className="itcd-card">
              <h2 className="itcd-card__heading">About This Course</h2>
              <p className="itcd-card__text">{course.description}</p>
            </div>

            {/* What you will learn */}
            <div className="itcd-card">
              <h2 className="itcd-card__heading">What You Will Learn</h2>
              <ul className="itcd-highlights">
                {course.highlights.map((h, i) => (
                  <li key={i} className="itcd-highlights__item">
                    <span className="itcd-highlights__check">✔</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Curriculum */}
            <div className="itcd-card">
              <h2 className="itcd-card__heading">Course Curriculum</h2>
              <div className="itcd-curriculum">
                {course.curriculum.map((phase, idx) => (
                  <div key={idx} className="itcd-phase">
                    <div className="itcd-phase__head">
                      <span className="itcd-phase__num">{String(idx + 1).padStart(2, '0')}</span>
                      <div>
                        <p className="itcd-phase__week">{phase.week}</p>
                        <h4 className="itcd-phase__topic">{phase.topic}</h4>
                      </div>
                    </div>
                    <ul className="itcd-phase__items">
                      {phase.items.map((item, j) => (
                        <li key={j} className="itcd-phase__item">
                          <span className="itcd-phase__dot" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Career paths */}
            <div className="itcd-card">
              <h2 className="itcd-card__heading">Career Opportunities</h2>
              <div className="itcd-careers">
                {course.career.map((c, i) => (
                  <span key={i} className="itcd-career-tag">{c}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: sidebar */}
          <aside className="itcd-sidebar">
            <div className="itcd-enq-card">
              <h3 className="itcd-enq-card__title">Enrol in This Course</h3>
              <ul className="itcd-enq-card__info">
                <li><span>⏱</span><span><strong>Duration:</strong> {course.duration}</span></li>
                <li><span>🎯</span><span><strong>Level:</strong> {course.level}</span></li>
                <li><span>💰</span><span><strong>Fee:</strong> {course.fee}</span></li>
                <li><span>🏆</span><span><strong>Certificate:</strong> Yes, on completion</span></li>
                <li><span>💼</span><span><strong>Placement:</strong> Included</span></li>
                <li><span>📍</span><span><strong>Mode:</strong> Online &amp; Offline (Ahmedabad)</span></li>
              </ul>
              <Link to="/it-training/contact" className="itcd-enq-card__btn">
                Enquire Now →
              </Link>
              <a href="https://wa.me/918758637022" target="_blank" rel="noreferrer" className="itcd-enq-card__wa">
                💬 Chat on WhatsApp
              </a>
            </div>

            <div className="itcd-why-box">
              <h4 className="itcd-why-box__title">Why Learn at Vedanskrix?</h4>
              <ul className="itcd-why-box__list">
                <li>✔ Live project training</li>
                <li>✔ Expert industry trainers</li>
                <li>✔ Certificate on completion</li>
                <li>✔ Job placement support</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <ITTrainingFooter />
    </div>
  );
}

export default CourseDetail;
